import useRefundTransaction from "api/mutations/transactions/useRefundTransaction"
import useGetTransactions from "api/querries/transactions/useGetTransactions"
import useDebounce from "hooks/useDebounce"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { useCustomersContext } from "providers/CustomersProvider/CustomersProvider"
import { useUI } from "providers/UIProvider"
import { createContext, useContext, useReducer, useMemo, useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { IGetTransactionsQueryParams } from "types/transaction"
import { TRANSACTIONS_INITIAL_CONTEXT, TRANSACTIONS_INITIAL_STATE } from "./TransactionsProviderConstants"

import { ITransactionsContext, ITransactionsProviderProps } from "./TransactionsProviderTypes"

export const TransactionsContext = createContext<ITransactionsContext>(TRANSACTIONS_INITIAL_CONTEXT)

export const useTransactionsContext: () => ITransactionsContext = () => useContext(TransactionsContext)


const TransactionsProvider: React.FC<ITransactionsProviderProps> = ({ children }: ITransactionsProviderProps) => {
  const { search, pathname } = useLocation()
  const navigate = useNavigate()

  const { toggleModal, modals } = useUI()
  const { selectedBranches } = useAppContext()
  const { currentCustomer } = useCustomersContext()

  const searchParams = new URLSearchParams(search)
  const isCustomersPage = pathname === '/customers'

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    TRANSACTIONS_INITIAL_STATE,
  )

  const debouncedSearchValue = useDebounce(state.searchValue, 1000)

  let query: IGetTransactionsQueryParams = {
    pageIndex: Number(searchParams.get("pageIndex")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 10,
    sortBy: searchParams.get("sortBy") || "id",
    search: debouncedSearchValue ? debouncedSearchValue : currentCustomer?.userName,
  }

  // : debouncedSearchValue ? debouncedSearchValue : searchParams.get("search") || "",

  const fetchTransactions = isCustomersPage ? (query.search !== undefined && modals.clientTransactionsModal.isOpened) : true

  const { data: transactionsData, isFetched: transactionsDataFetched, refetch: refetchTransactionsData } = useGetTransactions({
    key: isCustomersPage ? "customer_transactions" : "transactions",
    query,
    enabled: fetchTransactions
  })

  const { mutate: refundTransaction, isSuccess: refundTransactionSuccess, isLoading: refundTransactionLoading } = useRefundTransaction()

  const updateSearchParams = (query: any) => {
    navigate({
      search: `?${new URLSearchParams({
        ...query
      }).toString()}`
    })
  }

  const handlePaginationClick = (newPage: number) => {


    const updatedQuery = {
      ...query,
      pageIndex: newPage
    }
    updateSearchParams(updatedQuery);

    // Update the local query variable
    query = updatedQuery;

    // Refetch data with the updated query
    refetchTransactionsData();
  }

  const handleSearchChange = (e: any) => setState({ searchValue: e.target.value })

  useEffect(() => {
    if (transactionsData && transactionsDataFetched) {
      setState({ transactions: transactionsData.data })
    }
  }, [transactionsData])


  useEffect(() => {
    if (selectedBranches !== undefined) {
      query = {
        ...query,
        carWashIds: selectedBranches
      }
    }
  }, [selectedBranches])

  useEffect(() => {
    if (debouncedSearchValue !== undefined && transactionsDataFetched) {


      const udpatedQuery = {
        ...query,
        search: debouncedSearchValue || "",
        pageIndex: 1,
      }
      updateSearchParams(udpatedQuery)
      query = udpatedQuery;
    }
  }, [debouncedSearchValue])

  useEffect(() => {
    if (refundTransactionSuccess) {
      toggleModal("refundModal", false)
      refetchTransactionsData()
    }
  }, [refundTransactionSuccess])

  useEffect(() => {
    if (currentCustomer) {
      if (modals.clientTransactionsModal.isOpened) {
        query = {
          ...query,
          search: currentCustomer.userName
        }
      }
    }
  }, [currentCustomer])

  useEffect(() => {
    const updatedQuery = {
      pageIndex: Number(searchParams.get("pageIndex")) || 1,
      pageSize: Number(searchParams.get("pageSize")) || 10,
      sortBy: searchParams.get("sortBy") || "id",
      search: searchParams.get("search") || "",
    }

    if (searchParams.get("search")) {
      setState({
        searchValue: searchParams.get("search")
      })
    }

    query = updatedQuery
  }, [])

  const value = useMemo(() => ({
    ...state,
    query,
    handlePaginationClick,
    handleSearchChange,
    refundTransaction,
    refundTransactionLoading
  }), [state, query, refundTransactionLoading])

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  )

}

export default TransactionsProvider
